import { useEffect, useMemo, useState } from 'react';
import {
  Button, Combobox, Dropdown, Field, Option, Textarea, Text,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
  makeStyles, tokens,
} from '@fluentui/react-components';
import { useT } from '../../i18n';
import { SearchableSelect } from '../shared/SearchableSelect';
import { NumberInput } from '../shared/NumberInput';
import {
  MARKETS, MRL_UNITS, type ComplianceRule, type ComplianceRuleInput, type Market, type MrlUnit,
} from '../../api/compliance-rules.api';

const useStyles = makeStyles({
  form: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, minWidth: '320px' },
  row:  { display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  grow: { flex: '1 1 140px' },
  error: { color: tokens.colorPaletteRedForeground1, fontSize: tokens.fontSizeBase200 },
});

export interface ComplianceRuleDialogProps {
  open: boolean;
  /** Regla a editar; sin ella el diálogo da de alta. */
  rule?: ComplianceRule | null;
  products: { id: string; label: string }[];
  crops: string[];
  saving?: boolean;
  serverError?: string;
  onCancel: () => void;
  onSubmit: (input: ComplianceRuleInput) => void;
}

interface FormState {
  productId: string; crop: string; market: Market | '';
  phiDays: string; reiHours: string; mrl: string; mrlUnit: MrlUnit; notes: string;
}

function initial(rule?: ComplianceRule | null): FormState {
  return {
    productId: rule?.productId ?? '',
    crop: rule?.crop ?? '',
    market: rule?.market ?? '',
    phiDays: rule ? String(rule.phiDays) : '',
    reiHours: rule?.reiHours !== undefined ? String(rule.reiHours) : '',
    mrl: rule ? String(rule.mrlValue ?? rule.mrlMgPerKg) : '',
    mrlUnit: (rule?.mrlUnit as MrlUnit) ?? 'mg/kg',
    notes: rule?.notes ?? '',
  };
}

export function ComplianceRuleDialog({ open, rule, products, crops, saving, serverError, onCancel, onSubmit }: ComplianceRuleDialogProps) {
  const s = useStyles();
  const t = useT();
  const c = t.complianceRules;
  const [form, setForm] = useState<FormState>(() => initial(rule));
  const [missing, setMissing] = useState(false);

  // Reinicia el formulario cada vez que se abre (alta o edición de otra regla).
  useEffect(() => {
    if (open) { setForm(initial(rule)); setMissing(false); }
  }, [open, rule]);

  const cropOptions = useMemo(() => {
    const q = form.crop.trim().toLowerCase();
    return crops.filter((x) => !q || x.toLowerCase().includes(q));
  }, [crops, form.crop]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));

  const submit = () => {
    const phi = Number(form.phiDays);
    const rei = Number(form.reiHours);
    const mrl = Number(form.mrl);
    if (!form.productId || !form.crop.trim() || !form.market || form.phiDays === '' || form.reiHours === '' || form.mrl === ''
      || [phi, rei, mrl].some((n) => !Number.isFinite(n))) {
      setMissing(true);
      return;
    }
    onSubmit({
      productId: form.productId,
      crop: form.crop.trim(),
      market: form.market,
      phiDays: phi,
      reiHours: rei,
      mrl,
      mrlUnit: form.mrlUnit,
      ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) onCancel(); }}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{rule ? c.dialogEditTitle : c.dialogNewTitle}</DialogTitle>
          <DialogContent>
            <div className={s.form}>
              <SearchableSelect
                label={c.fieldProduct}
                options={products}
                value={form.productId}
                onChange={(id) => set('productId', id)}
              />
              <Field label={c.fieldCrop} hint={c.fieldCropHint}>
                <Combobox
                  freeform
                  aria-label={c.fieldCrop}
                  value={form.crop}
                  onOptionSelect={(_, d) => { /* Con `freeform`, Fluent también avisa al teclear (sin opción): se ignora. */ if (d.optionValue === undefined) return; set('crop', d.optionText ?? ''); }}
                  onInput={(e) => set('crop', (e.target as HTMLInputElement).value)}
                >
                  {cropOptions.map((x) => <Option key={x} value={x}>{x}</Option>)}
                </Combobox>
              </Field>
              <Field label={c.fieldMarket}>
                <Dropdown
                  aria-label={c.fieldMarket}
                  value={form.market}
                  selectedOptions={form.market ? [form.market] : []}
                  onOptionSelect={(_, d) => set('market', d.optionValue as Market)}
                >
                  {MARKETS.map((m) => <Option key={m} value={m}>{m}</Option>)}
                </Dropdown>
              </Field>
              <div className={s.row}>
                <Field label={c.fieldPhi} className={s.grow}>
                  <NumberInput aria-label={c.fieldPhi} value={form.phiDays} maxDecimals={2} onValueChange={(v) => set('phiDays', v)} />
                </Field>
                <Field label={c.fieldRei} className={s.grow}>
                  <NumberInput aria-label={c.fieldRei} value={form.reiHours} maxDecimals={2} onValueChange={(v) => set('reiHours', v)} />
                </Field>
              </div>
              <div className={s.row}>
                <Field label={c.fieldMrl} className={s.grow}>
                  <NumberInput aria-label={c.fieldMrl} value={form.mrl} maxDecimals={4} onValueChange={(v) => set('mrl', v)} />
                </Field>
                <Field label={c.fieldMrlUnit}>
                  <Dropdown
                    aria-label={c.fieldMrlUnit}
                    value={form.mrlUnit}
                    selectedOptions={[form.mrlUnit]}
                    onOptionSelect={(_, d) => set('mrlUnit', d.optionValue as MrlUnit)}
                  >
                    {MRL_UNITS.map((u) => <Option key={u} value={u}>{u}</Option>)}
                  </Dropdown>
                </Field>
              </div>
              <Field label={c.fieldNotes}>
                <Textarea value={form.notes} maxLength={500} onChange={(_, d) => set('notes', d.value)} />
              </Field>
              {missing && <Text className={s.error}>{c.requiredFields}</Text>}
              {serverError && <Text className={s.error} role="alert">{serverError}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onCancel}>{c.cancel}</Button>
            <Button appearance="primary" onClick={submit} disabled={saving}>{saving ? c.saving : c.save}</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
